import { GlobalStateController } from "bai-react-global-state";
import * as flexsearch from "flexsearch";
import { auth } from "js/firebase/auth";
import _ from "lodash";

import { PusherEventType } from "common/constants";
import { fetchCompressedIndexData, getEncoderOptions, search as performSearch } from "common/flexsearch";
import {
    IWorkspaceDocumentChangedEvent,
    IWorkspacePermission,
    IWorkspaceResource,
    IWorkspaceResourceDocumentUpdatedEvent,
    IWorkspaceResourceWithDocument,
    PaginatedFilter,
    PaginatedResponse,
    PaginatedSort,
    WorkspacePermissionObjectType,
    WorkspacePermissionSubjectType,
    WorkspaceResourceType
} from "common/interfaces";
import getObjectHash from "common/utils/getObjectHash";
import { getSequentialRunner, SequentialRunner } from "common/utils/runSequentially";
import { serverContext } from "js/config";
import WorkspaceController, { getWorkspaceResources, getWorkspaceResourcesTags, getWorkspaceResourcesWithDocuments } from "js/controllers/WorkspaceController";
import getLogger from "js/core/logger";
import pusher, { ExtendedChannel } from "js/core/services/pusher";

const logger = getLogger();

export interface BaseWorkspaceResourcesControllerState<TDocument, TResource> {
    filters: PaginatedFilter[];
    sort: PaginatedSort[];
    total: number;
    data: IWorkspaceResourceWithDocument<TDocument, TResource>[];
    fetching: boolean;
    initialized: boolean;
    tags: {
        tag: string;
        count: number;
    }[];
    search: {
        query: string;
        fields?: string[];
    }
    resourceTypes: WorkspaceResourceType[];
    workspaceId: string;
}

export const BASE_INITIAL_STATE: BaseWorkspaceResourcesControllerState<unknown, IWorkspaceResource> = {
    filters: [],
    sort: [],
    total: 0,
    data: [],
    search: {
        query: ""
    },
    fetching: false,
    initialized: false,
    tags: [],
    resourceTypes: [],
    workspaceId: null,
};

export class BaseWorkspaceResourcesController<
    TDocument,
    TResource extends IWorkspaceResource,
    TSearchDocument,
    TState extends BaseWorkspaceResourcesControllerState<TDocument, TResource> = BaseWorkspaceResourcesControllerState<TDocument, TResource>
> extends GlobalStateController<TState> {
    protected _runSequentially: SequentialRunner;
    protected _index: flexsearch.Document<any>;
    protected _indexPromise: Promise<void>;
    protected _workspaceUpdatesPusherChannel: ExtendedChannel;
    protected _workspaceResourceDocumentUpdatesPusherChannel: ExtendedChannel;
    protected _unsubscribeWorkspaceResourceDocumentUpdatesPusherChannel: () => void;
    protected _unsubscribeWorkspaceResourcesChanged: () => void;
    protected _reloadDataKey: number = 0;
    protected _loadMoreDataKey: number = 0;
    protected _reloadTagsKey: number = 0;

    constructor(initialState: TState = BASE_INITIAL_STATE as TState) {
        super(_.cloneDeep(initialState));

        this._runSequentially = getSequentialRunner();
    }

    public get initialized() {
        return this._state.initialized;
    }

    protected get _baseFilters(): PaginatedFilter[] {
        return [];
    }

    protected get _batchSize() {
        return 20;
    }

    protected get _documentFields(): string[] {
        throw new Error("Not implemented");
    }

    protected _searchDocumentToResourceWithDocument(searchDocument: TSearchDocument): IWorkspaceResourceWithDocument<TDocument, TResource> {
        throw new Error("Not implemented");
    }

    protected async _fetchSearchIndex({ locale, workspaceId }: { locale: string, workspaceId: string }): Promise<{ indexConfig: flexsearch.DocumentOptions<any>, indexFileReadUrl: string }> {
        throw new Error("Not implemented");
    }

    private _updateBaseState(state: Partial<BaseWorkspaceResourcesControllerState<TDocument, TResource>> | ((prevState: BaseWorkspaceResourcesControllerState<TDocument, TResource>) => BaseWorkspaceResourcesControllerState<TDocument, TResource>)) {
        return this._updateState(state as Partial<TState> | ((prevState: TState) => TState));
    }

    protected async _prepareIndex() {
        const { workspaceId } = this._state;

        // Wait for a tick to let the caller set this._indexPromise value so we can capture it
        await new Promise(resolve => setTimeout(resolve, 0));
        const currentIndexPromise = this._indexPromise;

        const locale = serverContext.locale ?? "en";
        const { indexConfig, indexFileReadUrl } = await this._fetchSearchIndex({ locale, workspaceId });

        const data = await fetchCompressedIndexData(indexFileReadUrl);

        if (currentIndexPromise !== this._indexPromise) {
            // New request came in
            return;
        }

        const index = new flexsearch.Document({
            encoder: getEncoderOptions(locale),
            ...indexConfig,
        });
        data.forEach(({ key, data }) => index.import(key, data));

        this._index = index;
    }

    protected async _ensureIndexPrepared() {
        if (!this._indexPromise) {
            this._indexPromise = this._prepareIndex();
        }
        return this._indexPromise;
    }

    protected _disposeIndex() {
        this._indexPromise = null;
        this._index = null;
    }

    /**
     * Client-side filtering of records, used for index-derived filtering and post-update filtering
     *
     * !!!! MUST stay in sync with common-backend/mongo/aggregations/Pagination.ts !!!!
     */
    protected _filterRecords(records: IWorkspaceResourceWithDocument<TDocument, TResource>[], filters: PaginatedFilter[]) {
        for (const filter of filters) {
            records = records.filter(item => {
                const value = _.get(item, filter.field);
                const filterValueArray = Array.isArray(filter.value) ? filter.value : [filter.value];

                switch (filter.operator) {
                    case "eq":
                        if (Array.isArray(value)) {
                            return value.includes(filter.value);
                        }
                        if (filter.value === null) {
                            return value == null; // Non-strict equality check for null values
                        }
                        return value === filter.value;
                    case "ne":
                        if (Array.isArray(value)) {
                            return !value.includes(filter.value);
                        }
                        if (filter.value === null) {
                            return value != null; // Non-strict equality check for null values
                        }
                        return value !== filter.value;
                    case "gt":
                        return value > filter.value;
                    case "gte":
                        return value >= filter.value;
                    case "lt":
                        return value < filter.value;
                    case "lte":
                        return value <= filter.value;
                    case "in":
                        return filterValueArray.includes(value);
                    case "nin":
                        return !filterValueArray.includes(value);
                }
                return true;
            });
        }

        return records;
    }

    protected async _fetchResourcesFromIndex({
        offset,
        size
    }: {
        offset: number;
        size: number;
    }) {
        const { filters, search } = this._state;

        await this._ensureIndexPrepared();

        const results = performSearch({
            index: this._index,
            query: search.query,
            fields: search.fields as flexsearch.FieldName<TSearchDocument>[]
        });

        let items: IWorkspaceResourceWithDocument<TDocument, TResource>[] = [];
        results.forEach(result => {
            const resourceWithDocument = this._searchDocumentToResourceWithDocument(result);
            if (!resourceWithDocument) {
                return;
            }

            if (items.some(item => item.resource.id === resourceWithDocument.resource.id)) {
                return;
            }

            items.push(resourceWithDocument);
        });

        // Client-side filtering and pagination //

        items = this._filterRecords(items, filters);

        // Important: sorting is forced to be score based for full text search queries

        const total = items.length;

        items = items.slice(offset, offset + size);

        return {
            total,
            items
        } as PaginatedResponse<IWorkspaceResourceWithDocument<TDocument, TResource>>;
    }

    protected async _fetchResources({
        offset,
        size
    }: {
        offset: number;
        size: number;
    }) {
        const { filters, sort, resourceTypes, workspaceId, search } = this._state;

        if (search.query) {
            // Full text search query is present, fetch results from the index
            // and do client-side filtering and sorting
            return await this._fetchResourcesFromIndex({ offset, size });
        }

        return await getWorkspaceResourcesWithDocuments({
            workspaceId,
            resourceTypes,
            offset,
            size,
            sort,
            filters,
            documentFields: this._documentFields
        }) as PaginatedResponse<IWorkspaceResourceWithDocument<TDocument, TResource>>;
    }

    protected async _loadMoreData() {
        const key = ++this._loadMoreDataKey;

        await this._updateBaseState({ fetching: true });

        try {
            const { total, items } = await this._fetchResources({
                offset: this._state.data.length,
                size: this._batchSize
            });

            if (!this._state.initialized || this._loadMoreDataKey !== key) {
                return;
            }

            const ids = items.map(({ resource }) => resource.id);

            await this._updateBaseState(prevState => ({
                ...prevState,
                total,
                data: [..._.cloneDeep(prevState.data.filter(({ resource }) => !ids.includes(resource.id))), ...items],
                fetching: false
            }));
        } catch (err) {
            logger.error(err, "Failed to load more resources");

            if (this._state.initialized && this._loadMoreDataKey === key) {
                await this._updateBaseState({ fetching: false });
            }

            throw err;
        }
    }

    protected async _reloadData(clearCurrentState = false) {
        const key = ++this._reloadDataKey;
        this._loadMoreDataKey++;

        if (clearCurrentState) {
            await this._updateBaseState({ data: [], total: 0, fetching: true });
        } else {
            await this._updateBaseState({ fetching: true });
        }

        try {
            const { total, items } = await this._fetchResources({
                offset: 0,
                size: this._batchSize
            });

            if (!this._state.initialized || this._reloadDataKey !== key) {
                return;
            }

            await this._updateBaseState(prevState => ({
                ...prevState,
                total,
                data: items,
                fetching: false
            }));
        } catch (err) {
            logger.error(err, "Failed to reload resources");

            if (this._state.initialized && this._reloadDataKey === key) {
                await this._updateBaseState({ fetching: false });
            }

            throw err;
        }
    }

    protected _getFiltersForTags(filters: PaginatedFilter[]) {
        return filters
            .filter(f => f.field !== "resource.tags")
            .filter(f => f.field.startsWith("resource."))
            .map(f => ({
                ...f,
                field: f.field.replace("resource.", "")
            }));
    }

    protected async _fetchTags() {
        const { resourceTypes, workspaceId, filters } = this._state;

        return await getWorkspaceResourcesTags({
            workspaceId,
            resourceTypes,
            resourceFilters: this._getFiltersForTags(filters)
        });
    }

    protected async _reloadTags(clearCurrentState = false) {
        const key = ++this._reloadTagsKey;

        if (clearCurrentState) {
            await this._updateBaseState({ tags: [] });
        }

        try {
            const tags = await this._fetchTags();

            if (!this._state.initialized || this._reloadTagsKey !== key) {
                return;
            }

            await this._updateBaseState({ tags });
        } catch (err) {
            logger.error(err, "Failed to reload tags");
        }
    }

    protected _stateDidUpdate(prevState: TState) {
        this._runSequentially(async () => {
            const { initialized, resourceTypes, filters, sort, search } = this._state;
            if (!initialized) {
                return;
            }

            if (getObjectHash(prevState.resourceTypes) !== getObjectHash(resourceTypes)) {
                this._disposeIndex();

                // Not awaited, we don't want to chain data loading operations
                Promise.all([
                    this._reloadTags(true).catch(() => { }),
                    this._reloadData(true).catch(() => { })
                ]);
                return;
            }

            if (
                getObjectHash(prevState.filters) !== getObjectHash(filters) ||
                getObjectHash(prevState.sort) !== getObjectHash(sort) ||
                (prevState.search.query !== search.query || (search.query && getObjectHash(prevState.search.fields) !== getObjectHash(search.fields)))
            ) {
                const keepState = search.query && prevState.search.query;

                // Not awaited, we don't want to chain data loading operations
                this._reloadData(!keepState).catch(() => { });
                if (getObjectHash(this._getFiltersForTags(prevState.filters)) !== getObjectHash(this._getFiltersForTags(filters))) {
                    this._reloadTags().catch(() => { });
                }
                return;
            }
        });
    }

    protected _onWorkspaceControllerResourcesChanged = ({ added, removed, updated }: {
        added: IWorkspaceResource[],
        removed: { resourceId: string, resourceType: WorkspaceResourceType }[],
        updated: IWorkspaceResource[]
    }) => {
        this._runSequentially(async () => {
            const { initialized } = this._state;
            if (!initialized) {
                return;
            }

            if (removed.length > 0) {
                await this._removeRecords(removed.map(({ resourceId }) => resourceId));
            }

            const addedAndUpdated = [...added, ...updated];
            if (addedAndUpdated.length > 0) {
                await this._forceRefreshResources(addedAndUpdated.map(({ resourceId }) => resourceId));
            }
        });
    }

    protected _onWorkspaceResourceDocumentUpdatePusherEvent = (event: IWorkspaceResourceDocumentUpdatedEvent) => {
        this._runSequentially(async () => {
            const { resourceTypes, data, workspaceId, initialized } = this._state;
            if (!initialized) {
                return;
            }

            const { resource, document, permission, triggeredAt } = event;

            if (!resourceTypes.includes(resource.resourceType) || resource.workspaceId !== workspaceId) {
                return;
            }

            const recordBefore = data.find(record => record.resource.id === resource.id);
            if (!recordBefore || (recordBefore.document as { modifiedAt: number }).modifiedAt >= triggeredAt) {
                // This might make so we ignore an update that makes the updated presentation visible in current page,
                // we're willing to live with that to avoid re-fetching too much data
                return;
            }

            const record: IWorkspaceResourceWithDocument<TDocument, TResource> = {
                resource: resource as IWorkspaceResourceWithDocument<TDocument, TResource>["resource"],
                document,
                permission
            };
            await this._replaceRecords([record]);
        });
    }

    protected _onWorkspaceUpdatePusherEvent = (event: IWorkspaceDocumentChangedEvent) => {
        this._runSequentially(async () => {
            const { data, workspaceId, resourceTypes, initialized } = this._state;
            if (!initialized) {
                return;
            }

            if (event.documentType === "WorkspaceResource") {
                const workspaceResource = event.operationType === "delete" ? event.oldDocument : event.newDocument;

                if (event.operationType === "delete") {
                    await this._removeRecords([workspaceResource.resourceId]);
                    return;
                }

                const existingRecord = data.find(({ resource }) => resource.id === workspaceResource.id);
                if (existingRecord && existingRecord.resource.modifiedAt >= workspaceResource.modifiedAt) {
                    return;
                }

                await this._forceRefreshResource(workspaceResource.resourceId);
            }

            if (event.documentType === "WorkspacePermission") {
                if (event.operationType === "delete") {
                    const workspacePermission = event.oldDocument as IWorkspacePermission;
                    if (workspacePermission.objectType !== WorkspacePermissionObjectType.WORKSPACE_RESOURCE) {
                        return;
                    }

                    const id = workspacePermission.objectId;
                    const record = data.find(({ resource }) => resource.id === id);
                    if (!record) {
                        return;
                    }

                    await this._forceRefreshResource(record.resource.resourceId);
                    return;
                }

                const workspacePermission = event.newDocument as IWorkspacePermission;
                if (workspacePermission.objectType !== WorkspacePermissionObjectType.WORKSPACE_RESOURCE) {
                    return;
                }

                if (
                    (workspacePermission.subjectType === WorkspacePermissionSubjectType.USER && workspacePermission.subjectId === auth().currentUser.uid) ||
                    (workspacePermission.subjectType === WorkspacePermissionSubjectType.USER_GROUP && WorkspaceController.workspaceUserGroups.some(g => g.id === workspacePermission.subjectId))
                ) {
                    const id = workspacePermission.objectId;
                    // Fetch resource by resource record id
                    const [resource] = await getWorkspaceResources({
                        workspaceId,
                        resourceTypes: resourceTypes,
                        resourceFilters: [{ field: "_id", operator: "eq", value: id }]
                    });
                    if (resource) {
                        await this._forceRefreshResource(resource.resourceId);
                    }
                }
            }
        });
    }

    protected async _forceRefreshResource(resourceId: string) {
        return this._forceRefreshResources([resourceId]);
    }

    protected async _forceRefreshResources(resourceIds: string[]) {
        const { workspaceId, resourceTypes, initialized } = this._state;
        if (!initialized) {
            return;
        }

        const { items } = await getWorkspaceResourcesWithDocuments({
            workspaceId,
            resourceTypes,
            offset: 0,
            size: resourceIds.length,
            documentFields: this._documentFields,
            resourceFilters: [{ field: "resourceId", operator: "in", value: resourceIds }]
        });

        const resourceIdsToRemove = resourceIds.filter(resourceId => !items.some(item => item.resource.resourceId === resourceId));
        if (resourceIdsToRemove.length > 0) {
            await this._removeRecords(resourceIdsToRemove);
        }

        if (items.length > 0) {
            await this._replaceRecords(items);
        }
    }

    protected async _removeRecords(resourceIds: string[]) {
        const { data } = this._state;

        let idsToRemove: string[] = [];
        for (const resourceId of resourceIds) {
            const existingRecords = data.filter(({ resource }) => resource.resourceId === resourceId);
            idsToRemove.push(...existingRecords.map(({ resource }) => resource.id));
        }

        if (idsToRemove.length === 0) {
            return;
        }

        this._disposeIndex();

        await this._updateBaseState(state => ({
            ...state,
            data: state.data.filter(({ resource }) => !idsToRemove.includes(resource.id))
        }));

        // Not awaited, we don't want to chain data loading operations
        this._reloadTags().catch(() => { });
    }

    protected async _replaceRecords(records: IWorkspaceResourceWithDocument<TDocument, TResource>[]) {
        const { data, resourceTypes } = this._state;

        records = records.filter(record => resourceTypes.includes(record.resource.resourceType));
        if (records.length === 0) {
            return;
        }

        this._disposeIndex();

        let shouldFetchTags = false;
        for (const record of records) {
            const recordBefore = data.find(({ resource }) => resource.id === record.resource.id);
            if (!recordBefore || getObjectHash(recordBefore.resource.tags) !== getObjectHash(record.resource.tags)) {
                shouldFetchTags = true;
            }
        }

        await this._updateBaseState(state => {
            let addedRecordCount = 0;
            let data = _.cloneDeep(state.data);
            for (const record of records) {
                data = data.map(item => item.resource.id === record.resource.id ? record : item);
                if (!data.some(({ resource }) => resource.id === record.resource.id)) {
                    data.push(record);
                    addedRecordCount++;
                }
            }

            if (state.sort.length > 0 && !state.search.query) {
                // Sorting may have changed after the resource was refreshed
                for (const sort of [...state.sort].reverse()) {
                    data = data.sort((a, b) => {
                        let valueA = _.get(a, sort.field);
                        let valueB = _.get(b, sort.field);

                        if (typeof valueA === "string" && typeof valueB === "string") {
                            valueA = valueA.toLowerCase();
                            valueB = valueB.toLowerCase();
                        }

                        if (valueA === valueB) {
                            return 0;
                        }

                        const valueAMissing = valueA == null;
                        const valueBMissing = valueB == null;
                        if (valueAMissing || valueBMissing) {
                            return sort.direction === "asc"
                                ? (valueAMissing ? -1 : 1)
                                : (valueAMissing ? 1 : -1);
                        }

                        return sort.direction === "asc"
                            ? (valueA > valueB ? 1 : -1)
                            : (valueA < valueB ? 1 : -1);
                    });
                }
            }

            if (state.filters.length > 0) {
                // Filtering may have changed after the resource was refreshed
                data = this._filterRecords(data, state.filters);
            }

            return {
                ...state,
                // No need to be super precise here, just increment by the number of added records
                // to make sure we're no blocking load more
                total: state.total + addedRecordCount,
                data
            };
        });

        if (shouldFetchTags) {
            // Not awaited, we don't want to chain data loading operations
            this._reloadTags().catch(() => { });
        }
    }

    public get canLoadMore() {
        const { data, total } = this._state;

        if (data.length === 0) {
            return false;
        }

        return data.length < total;
    }

    public async setFilters(filters: TState["filters"]) {
        return this._runSequentially(() => this._updateBaseState({ filters }));
    }

    public async setSort(sort: TState["sort"]) {
        return this._runSequentially(() => this._updateBaseState({ sort }));
    }

    public async setSearch(search: TState["search"]) {
        return this._runSequentially(() => this._updateBaseState({ search }));
    }

    public async loadMore() {
        // Not awaited, we don't want to chain data loading operations
        return this._loadMoreData();
    }

    public async reload() {
        // Not awaited, we don't want to chain data loading operations
        return this._reloadData(true);
    }

    public async reset(initialState: TState = BASE_INITIAL_STATE as TState) {
        this._reloadDataKey++;
        this._loadMoreDataKey++;
        this._reloadTagsKey++;
        this._indexPromise = null;
        this._index = null;

        if (this._unsubscribeWorkspaceResourcesChanged) {
            this._unsubscribeWorkspaceResourcesChanged();
            this._unsubscribeWorkspaceResourcesChanged = null;
        }

        if (this._workspaceUpdatesPusherChannel) {
            this._workspaceUpdatesPusherChannel.unbind(PusherEventType.DATA_RECORD_UPDATED, this._onWorkspaceUpdatePusherEvent);
            if (!this._workspaceUpdatesPusherChannel.isInUse) {
                pusher.unsubscribe(this._workspaceUpdatesPusherChannel.name);
            }
            this._workspaceUpdatesPusherChannel = null;
        }

        if (this._workspaceResourceDocumentUpdatesPusherChannel) {
            if (this._unsubscribeWorkspaceResourceDocumentUpdatesPusherChannel) {
                this._unsubscribeWorkspaceResourceDocumentUpdatesPusherChannel();
                this._unsubscribeWorkspaceResourceDocumentUpdatesPusherChannel = null;
            }
            if (!this._workspaceResourceDocumentUpdatesPusherChannel.isInUse) {
                pusher.unsubscribe(this._workspaceResourceDocumentUpdatesPusherChannel.name);
            }
            this._workspaceResourceDocumentUpdatesPusherChannel = null;
        }

        await this._runSequentially(() => this._updateBaseState(_.cloneDeep(initialState)));
    }

    public async initialize(
        { filters, sort, search, resourceTypes, workspaceId, ...rest }:
            { filters?: PaginatedFilter[], sort?: PaginatedSort[], search?: TState["search"], resourceTypes: WorkspaceResourceType[], workspaceId: string } &
            Partial<Omit<TState, keyof BaseWorkspaceResourcesControllerState<TDocument, TResource>>>
    ) {
        await this.reset();

        await this._runSequentially(async () => {
            this._workspaceUpdatesPusherChannel = await pusher.subscribe(`private-workspace-${workspaceId}`);
            this._workspaceUpdatesPusherChannel.bind(PusherEventType.DATA_RECORD_UPDATED, this._onWorkspaceUpdatePusherEvent);

            this._workspaceResourceDocumentUpdatesPusherChannel = await pusher.subscribe(`private-workspace-resource-document-updates-${auth().currentUser.uid}`);
            this._unsubscribeWorkspaceResourceDocumentUpdatesPusherChannel = this._workspaceResourceDocumentUpdatesPusherChannel.bindChunked(PusherEventType.DATA_RECORD_UPDATED, this._onWorkspaceResourceDocumentUpdatePusherEvent);

            this._unsubscribeWorkspaceResourcesChanged = WorkspaceController.onWorkspaceResourcesChanged(this._onWorkspaceControllerResourcesChanged);

            await this._updateBaseState({
                ...rest,
                filters: filters ?? [],
                sort: sort ?? [],
                search: search ?? { query: "" },
                resourceTypes,
                workspaceId,
                initialized: true,
                fetching: true
            });

            // Data and tags load will be triggered by stateDidUpdate
        });
    }
}
